import * as React from "react"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Badge Component - INFLUENCITY-TIER
 * 
 * Contract:
 * - Text inside badges MUST meet AA (4.5:1) in BOTH modes
 * - Governance variants distinguishable from each other
 * - Border + background + text all contribute to visibility
 * - Small badges must meet 4.5:1 minimum
 * - Danger = rose (non-alarming), not harsh red
 * 
 * All colors route through granullo.tokens.css
 */

const badgeVariants = cva(
  // Base styles
  "inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--g-focus-ring)]",
  {
    variants: {
      variant: {
        // Default: primary blue with WHITE text (g-on-primary)
        default:
          "border-[var(--g-btn-primary-border)] bg-[var(--g-primary)] text-[var(--g-on-primary)] shadow-[var(--g-shadow-low)]",
        // Secondary: glass surface
        secondary:
          "border-[var(--g-pill-border)] bg-[var(--g-pill-bg)] text-[var(--g-pill-text)]",
        // Destructive: rose (non-alarming)
        destructive:
          "border-[var(--g-badge-danger-border)] bg-[var(--g-badge-danger-bg)] text-[var(--g-badge-danger-text)]",
        // Outline: transparent with border
        outline: 
          "text-[var(--g-text)] border-[var(--g-border)] bg-transparent",
        // Calm: soft indigo
        calm:
          "bg-[var(--g-badge-calm-bg)] text-[var(--g-badge-calm-text)] border-[var(--g-badge-calm-border)]",
        // Governance primitives
        intent:
          "bg-[var(--g-badge-intent-bg)] text-[var(--g-badge-intent-text)] border-[var(--g-badge-intent-border)]",
        evidence:
          "bg-[var(--g-badge-evidence-bg)] text-[var(--g-badge-evidence-text)] border-[var(--g-badge-evidence-border)]",
        drift:
          "bg-[var(--g-badge-drift-bg)] text-[var(--g-badge-drift-text)] border-[var(--g-badge-drift-border)]",
        responsibility:
          "bg-[var(--g-badge-resp-bg)] text-[var(--g-badge-resp-text)] border-[var(--g-badge-resp-border)]",
        // State variants
        success:
          "bg-[var(--g-badge-success-bg)] text-[var(--g-badge-success-text)] border-[var(--g-badge-success-border)]",
        warning:
          "bg-[var(--g-badge-warning-bg)] text-[var(--g-badge-warning-text)] border-[var(--g-badge-warning-border)]",
        danger:
          "bg-[var(--g-badge-danger-bg)] text-[var(--g-badge-danger-text)] border-[var(--g-badge-danger-border)]",
        info:
          "bg-[var(--g-badge-info-bg)] text-[var(--g-badge-info-text)] border-[var(--g-badge-info-border)]",
      },
      size: {
        default: "px-2.5 py-0.5 text-xs",
        sm: "px-2 py-0.5 text-[11px] leading-tight",
        lg: "px-3 py-1 text-sm",
      },
      pill: {
        true: "rounded-[var(--g-pill-radius)]",
        false: "rounded-[var(--g-radius-sm)]",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      pill: false,
    },
  }
)

function Badge({
  className,
  variant,
  size,
  pill,
  ...props
}) {
  return (<div className={cn(badgeVariants({ variant, size, pill }), className)} {...props} />);
}

export { Badge, badgeVariants }
